import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from './ToastContext';
import './PackageEdit.css';

const PackageEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  const API_BASE_URL = 'https://eks-projectmanager.eksconstruction.in/api';

  useEffect(() => {
    const fetchPackage = async () => {
      try {
        const resp = await fetch(`${API_BASE_URL}/get_packages.php`);
        const result = await resp.json();
        if (result.success && result.packages) {
          const found = result.packages.find(p => String(p.id) === String(id));
          if (found) {
            setForm({
              id: found.id,
              title: found.title,
              price: found.price,
              color: found.color,
              popular: !!found.popular,
              sections: found.sections || {}
            });
          }
        }
      } catch (e) {
        showToast && showToast({ message: 'Failed to load package: ' + e.message, type: 'error' });
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchPackage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleItemChange = (sectionName, itemIndex, value) => {
    setForm(prev => {
      const items = [...prev.sections[sectionName].items];
      items[itemIndex] = value;
      return {
        ...prev,
        sections: {
          ...prev.sections,
          [sectionName]: { items }
        }
      };
    });
  };

  const addItem = (sectionName) => {
    setForm(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionName]: { items: [...prev.sections[sectionName].items, ''] }
      }
    }));
  };

  const removeItem = (sectionName, itemIndex) => {
    setForm(prev => {
      const items = prev.sections[sectionName].items.filter((_, i) => i !== itemIndex);
      return {
        ...prev,
        sections: {
          ...prev.sections,
          [sectionName]: { items }
        }
      };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/update_package.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: form.id,
          title: form.title,
          price: form.price,
          color: form.color,
          popular: form.popular,
          sections: form.sections
        })
      });
      const result = await resp.json();
      if (result.success) {
        showToast && showToast({ message: 'Package updated successfully', type: 'success' });
        navigate('/packages');
      } else {
        showToast && showToast({ message: 'Update failed: ' + (result.error || 'Unknown error'), type: 'error' });
      }
    } catch (e) {
      showToast && showToast({ message: 'Update error: ' + e.message, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading package...</p>
      </div>
    );
  }

  if (!form) {
    return (
      <div style={{ padding: 40 }}>
        <p>Package not found</p>
        <button className="back-btn" onClick={() => navigate('/packages')}>Back</button>
      </div>
    );
  }

  return (
    <div className="package-edit-container">
      <div className="details-header">
        <button className="back-btn" onClick={() => navigate('/packages')}>Back to Packages</button>
      </div>

      <form className="package-edit-form" onSubmit={handleSave}>
        <div className="package-basic-fields">
          <label>
            Title
            <input value={form.title} onChange={e => handleField('title', e.target.value)} />
          </label>
          <label>
            Price (per sqft)
            <input value={form.price} onChange={e => handleField('price', e.target.value)} />
          </label>
          <label>
            Color theme
            <select value={form.color} onChange={e => handleField('color', e.target.value)}>
              <option value="blue">Blue</option>
              <option value="red">Red</option>
            </select>
          </label>
          <label className="checkbox-label">
            <input type="checkbox" checked={form.popular} onChange={e => handleField('popular', e.target.checked)} />
            Mark as "Most Popular"
          </label>
        </div>

        {Object.keys(form.sections).map(sectionName => (
          <div key={sectionName} className="section-editor">
            <h2 className="section-title">{sectionName}</h2>
            {form.sections[sectionName].items.map((item, idx) => (
              <div key={idx} className="section-item-row">
                <input
                  value={item}
                  onChange={e => handleItemChange(sectionName, idx, e.target.value)}
                />
                <button type="button" className="remove-item-btn" onClick={() => removeItem(sectionName, idx)}>✕</button>
              </div>
            ))}
            <button type="button" className="add-item-btn" onClick={() => addItem(sectionName)}>+ Add item</button>
          </div>
        ))}

        <div className="package-edit-actions">
          <button className="back-btn mark-btn" type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PackageEdit;
